<template>
  <div class="education-catalog-page">
    <!-- Header Section -->
    <PageHeader :title="catalogTitle" icon="uil-books">
      <template #right>
        <search-input v-model="searchQuery" @search="handleApplyFilter" />
      </template>
    </PageHeader>

    <!-- Credit Card Modal -->
    <credit-card-modal ref="creditCardModal" :format-price="formatPrice"
      :ordered-item-list-for-buying="orderedItemListForBuying" :token="token" />

    <div class="catalog-layout">
      <!-- Left Sidebar Filters -->
      <div class="catalog-filters-wrapper">
        <catalog-filters :catalogs-loading="catalogsLoading" :education-types="educationTypes"
          :formatted-training-kind="formattedTrainingKind" :has-url-filters="hasUrlFilters"
          :institution-training="institutionTraining" :kinds.sync="selectedKinds" :options.sync="selectedOptions"
          :subdomain="subdomain" @apply-filter="handleApplyFilter" />
      </div>

      <!-- Main Content Area -->
      <div class="catalog-content">
        <div class="card rounded">
          <!-- Tabs -->
          <catalog-tabs :active-tab.sync="activeTabIndex" :counts="trainingCounts" :loading="catalogsLoading"
            :total-filtered-training="totalFilteredTraining" @tab-change="handleTabChange" />

          <!-- Loading Skeleton -->
          <catalog-card-list-skeleton v-if="catalogsLoading" :count="5" />

          <!-- Empty State -->
          <div v-else-if="isEmptyState" class="empty-assignment-state">
            <div class="empty-assignment-icon">
              <i class="uil-books"></i>
            </div>
            <h4 class="empty-assignment-title">
              {{ $t('general.no_result_found') || 'Veri Bulunamadı' }}
            </h4>
          </div>

          <!-- Training Cards -->
          <catalog-card-list v-else :filters="computedFilters" :format-price="formatPrice"
            :get-bottom-title="getBottomTitle" :items="displayItems" @demo="handleDemo" @view-card="handleViewCard"
            @self-training="handleSelfTraining" @request-training="handleRequestTraining" />
        </div>

        <!-- Pagination (web) -->
        <Pagination v-if="shouldShowPagination && !isMobile" :page.sync="currentPage" :paginate="items" />

        <!-- Load More (mobile) -->
        <load-more-button v-if="isMobile" :visible="displayItems.length > 0" :has-more="hasMorePages"
          :loading="loadingMore" @load-more="handleLoadMore" />

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import moment from "moment";
import _ from "lodash";
import SecureLS from "secure-ls";
import store from "@/core/services";
import { formatPrice } from "@/utils/money";

import {
  CREATE_ITEM,
  GET_COUNTS,
  GET_ITEMS,
  ITEMS,
  UPDATE_ITEM_BY_ID
} from "@/core/services/store/REST.module";

import training_kind_module, {
  BASE_URL as TRAINING_KIND_URL,
  GET_ITEMS as GET_TRAINING_KINDS,
  ITEMS as TRAINING_KINDS,
  MODULE_NAME as MODULE_TRAINING_KIND,
} from "@/core/services/store/training_kind.module";

import EducationCard from "@/view/components/index/EducationCard.vue";
import Pagination from "@/view/components/dataTable/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import CatalogTabs from "@/view/User/EducationCatalog/components/CatalogTabs.vue";
import CatalogCardList from "@/view/User/EducationCatalog/components/CatalogCardList.vue";
import CatalogFilters from "@/view/User/EducationCatalog/components/CatalogFilters.vue";
import CatalogCardListSkeleton from "@/view/User/EducationCatalog/components/CatalogCardListSkeleton.vue";
import SearchInput from "@/view/components/inputs/SearchInput.vue";
import CreditCardModal from "@/view/User/EducationCatalog/components/CreditCardModal.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import LoadMoreButton from "@/view/User/MyAssignments/components/LoadMoreButton.vue";

const STORAGE = new SecureLS({ encodingType: "aes", isCompression: true });
const TRAINING_KIND_MODULE = MODULE_TRAINING_KIND;
const API_ENDPOINTS = {
  TRAINING: "api/training",
  TRAINING_COUNTS: "api/training-counts",
  TRAINING_REQUEST: "api/training/request/",
  TRAINING_CATEGORY: "api/training-type",
  SELF_REGISTRABLE: "api/trainings/",
  PAYMENT_INIT: "api/payment/init",
};

const TAB_KEYS = {
  0: "can_register_trainings",
  1: "show_registered_trainings",
  2: "requests_trainings",
};

export default {
  name: "EducationCatalog",

  components: {
    EducationCard,
    Pagination,
    DefaultLoading,
    CatalogTabs,
    CatalogCardList,
    CatalogFilters,
    CatalogCardListSkeleton,
    CreditCardModal,
    SearchInput,
    PageHeader,
    LoadMoreButton,
  },

  beforeCreate() {
    if (!store?.state?.[TRAINING_KIND_MODULE]) {
      store.registerModule(TRAINING_KIND_MODULE, training_kind_module);
    }
  },

  data() {
    return {
      subdomain: null,
      token: "",
      searchQuery: "",
      selectedOptions: [0],
      selectedKinds: [],
      activeTabIndex: 0,
      currentPage: 1,
      catalogsLoading: true,

      educationTypes: [],
      institutionTraining: [],
      trainingCounts: [],
      totalFilteredTraining: null,
      orderedItemListForBuying: {},
      menuList: [],

      orderBy: "name",
      sort: "desc",

      // Mobile load-more state
      isMobile: false,
      mobileItems: [],
      loadingMore: false,
      mobileCurrentPage: 1,
    };
  },

  computed: {
    ...mapGetters({
      items: ITEMS,
    }),

    isCatalogTrainingVisible() {
      const menu = this.menuList || [];
      const catalogItem = menu.find(item => item.type === "education-category");
      return catalogItem?.status === 1 ?? false;
    },
    catalogTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find((item) => item.type === "education-category");
      return this.getLocalizedWidgetTitle(menuItem, this.$t("education.catalog_education"), this.$i18n?.locale);
    },

    trainingKinds() {
      return store.getters[`${TRAINING_KIND_MODULE}/${TRAINING_KINDS}`] || {};
    },

    formattedTrainingKind() {
      const data = this.trainingKinds?.data;
      if (!data) return [];

      return data.map((item) => ({
        id: item.id,
        value: item.id,
        name: item.name,
      }));
    },

    trainingItems() {
      return this.items?.data || [];
    },

    displayItems() {
      return this.isMobile ? this.mobileItems : this.trainingItems;
    },

    hasMorePages() {
      if (!this.items) return false;
      return this.mobileCurrentPage < (this.items.last_page || 1);
    },

    isEmptyState() {
      return !this.catalogsLoading && this.displayItems.length === 0;
    },

    shouldShowPagination() {
      return (
        !this.catalogsLoading &&
        this.items?.data?.length > 0 &&
        this.items?.last_page !== 1
      );
    },

    computedFilters() {
      const filters = {
        page: this.currentPage,
        search: this.searchQuery,
        order_by: this.orderBy,
        by_user: true,
        can_show_self_registrable_trainings: true,
        show_registered_trainings: this.activeTabIndex === 1,
        can_register_trainings: this.activeTabIndex === 0,
        requests_trainings: this.activeTabIndex === 0 || this.activeTabIndex === 2,
      };

      // Handle training type filters
      if (this.hasValidOptions) {
        filters["training_type_id[]"] = this.cleanedOptions;
      } else {
        filters.training_type_id = 0;
      }

      // Handle training kind filters
      if (this.hasValidKinds) {
        filters["training_kind_id[]"] = this.selectedKinds;
      } else {
        filters.training_kind_id = 0;
      }

      return filters;
    },

    hasValidOptions() {
      return (
        this.selectedOptions.length > 0 &&
        !(this.selectedOptions.length === 1 && this.selectedOptions[0] === 0)
      );
    },

    hasValidKinds() {
      return this.selectedKinds.length > 0;
    },

    cleanedOptions() {
      return this.selectedOptions.filter((opt) => opt !== 0);
    },

    hasUrlFilters() {
      const { education, trainingKinds } = this.$route.query;
      return !!(education || trainingKinds);
    },
  },

  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue && !this.isMobile) {
        this.handleApplyFilter();
      }
    },
  },

  async mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    await this.initialize();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },

  methods: {
    formatPrice,

    checkMobile() {
      this.isMobile = window.innerWidth <= 959;
    },

    resetMobileState() {
      this.mobileItems = [];
      this.mobileCurrentPage = 1;
    },

    async handleLoadMore() {
      if (this.loadingMore || !this.hasMorePages) return;

      this.loadingMore = true;
      this.mobileCurrentPage++;

      try {
        const filters = { ...this.computedFilters, page: this.mobileCurrentPage };
        const response = await this.$store.dispatch(GET_ITEMS, {
          url: API_ENDPOINTS.TRAINING,
          acceptPromise: true,
          showLoading: false,
          filters,
        });

        if (response?.data?.data) {
          this.mobileItems = [...this.mobileItems, ...response.data.data];
        }
      } catch (error) {
        console.error("Error loading more:", error);
        this.mobileCurrentPage--;
      } finally {
        this.loadingMore = false;
      }
    },

    // Initialization
    async initialize() {
      this.subdomain = window.location.hostname;
      this.loadMenuList();

      if (!this.isCatalogTrainingVisible) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.loadInitialData();
      await this.handleUrlState();
    },

    loadMenuList() {
      const data = STORAGE.get("dashboardMenuList");
      if (data?.menu) {
        this.menuList = data.menu;
      }
    },

    async loadInitialData() {
      try {
        const response = await this.$store.dispatch(GET_ITEMS, {
          url: API_ENDPOINTS.TRAINING_CATEGORY,
          acceptPromise: true,
        });

        if (response?.status) {
          this.categorizeTrainingTypes(response.data);
          await this.loadTrainingKinds();
          await this.loadTrainingCounts();
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    },

    categorizeTrainingTypes(data) {
      data.forEach((element) => {
        if (element.company_id === 1) {
          this.educationTypes.push(element);
        } else {
          this.institutionTraining.push(element);
        }
      });
    },

    async loadTrainingKinds() {
      await store.dispatch(`${TRAINING_KIND_MODULE}/${GET_TRAINING_KINDS}`, {
        url: TRAINING_KIND_URL,
      });
    },

    async loadTrainingCounts() {
      try {
        const result = await this.$store.dispatch(GET_COUNTS, {
          url: API_ENDPOINTS.TRAINING_COUNTS,
          acceptPromise: false,
          showLoading: true,
          by_user: true,
        });
        this.trainingCounts = result?.data || [];
      } catch (error) {
        console.error("Error loading training counts:", error);
      }
    },

    // URL State Management
    async handleUrlState() {
      await this.$nextTick();

      if (Object.keys(this.$route.query).length > 0) {
        this.loadFiltersFromUrl();
        this.openAccordions();
      } else {
        await this.fetchTrainings(true);
      }
    },

    loadFiltersFromUrl() {
      const { query } = this.$route;

      // Parse education types
      if (query.education) {
        const educationIds = query.education.split(",").map(Number);
        this.selectedOptions = [0, ...educationIds];
      } else {
        this.selectedOptions = [0];
      }

      // Parse training kinds
      if (query.trainingKinds) {
        const kindIds = query.trainingKinds.split(",").map(Number);
        this.selectedKinds = kindIds;
      } else {
        this.selectedKinds = [];
      }

      // Parse tab
      if (query.tab && query.tabId) {
        this.setActiveTab(query.tab, parseInt(query.tabId));
      }

      this.fetchTrainings(true);
    },

    updateUrlQuery() {
      const query = { ...this.$route.query };

      // Education types
      const educationIds = this.cleanedOptions;
      if (educationIds.length > 0) {
        query.education = educationIds.join(",");
      } else {
        delete query.education;
      }

      // Training kinds
      if (this.selectedKinds.length > 0) {
        query.trainingKinds = this.selectedKinds.join(",");
      } else {
        delete query.trainingKinds;
      }

      // Active tab
      if (this.activeTabIndex !== undefined) {
        query.tab = TAB_KEYS[this.activeTabIndex];
        query.tabId = this.activeTabIndex;
      }

      const currentQuery = this.$route.query;
      const keys = new Set([...Object.keys(currentQuery), ...Object.keys(query)]);
      const hasChanged = [...keys].some(k => String(currentQuery[k] ?? '') !== String(query[k] ?? ''));

      if (hasChanged) {
        this.$router.replace({ query }).catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });
      }
    },

    openAccordions() {
      this.$nextTick(() => {
        const sections = document.querySelectorAll(".side-list > li");
        sections.forEach((section) => section.classList.add("uk-open"));
      });
    },

    // Filter Handlers
    async handleApplyFilter(skipUrlUpdate = false) {
      if (!skipUrlUpdate) {
        this.updateUrlQuery();
      }
      await this.fetchTrainings(skipUrlUpdate);
    },

    async fetchTrainings(skipUrlUpdate = false) {
      this.catalogsLoading = true;

      try {
        const response = await this.$store.dispatch(GET_ITEMS, {
          url: API_ENDPOINTS.TRAINING,
          acceptPromise: false,
          showLoading: true,
          filters: this.computedFilters,
        });

        if (response) {
          this.totalFilteredTraining = response.data?.total;

          // Mobile: reset and sync accumulated items on fresh fetch
          if (this.isMobile) {
            this.mobileCurrentPage = 1;
            this.mobileItems = response.data?.data || [];
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error("Error fetching trainings:", error);
      } finally {
        this.catalogsLoading = false;
      }
    },

    // Tab Management
    handleTabChange({ filterKey, tabIndex }) {
      this.setActiveTab(filterKey, tabIndex);
    },

    setActiveTab(key, index) {
      this.activeTabIndex = index;
      this.currentPage = 1;
      this.resetTabFilters();

      const filters = this.computedFilters;
      filters[key] = "true";

      if (key === "can_register_trainings" || key === "requests_trainings") {
        filters.requests_trainings = true;
      } else {
        filters.requests_trainings = false;
      }

      this.handleApplyFilter();
    },

    resetTabFilters() {
      const filters = this.computedFilters;
      filters.show_registered_trainings = false;
      filters.can_register_trainings = false;
      filters.requests_trainings = false;
    },

    // Action Handlers
    handleViewCard(id) {
      //ilk tab eğitimelrinde demo gösterimi olmaycak
      if (this.activeTabIndex === 0)
        return

      this.$router.push({
        name: "user.education-detail",
        params: { id },
      });
    },

    handleDemo(id) {
      this.$router.push({
        name: "user.education-detail",
        params: { id }
      });
    },

    async handleRequestTraining(id) {
      try {
        const result = await this.$store.dispatch(CREATE_ITEM, {
          url: `${API_ENDPOINTS.TRAINING_REQUEST}${id}`,
          acceptPromise: true,
        });

        if (result) {
          await this.handleApplyFilter();
        }
      } catch (error) {
        console.error("Error requesting training:", error);
      }
    },

    async handleSelfTraining({ id, mode }) {
      if (mode === "register") {
        await this.registerForTraining(id);
      } else if (mode === "buy") {
        await this.initiatePurchase(id);
      }
    },

    async registerForTraining(id) {
      try {
        const result = await this.$store.dispatch(UPDATE_ITEM_BY_ID, {
          url: `${API_ENDPOINTS.SELF_REGISTRABLE}${id}/self-registrable`,
        });

        if (result) {
          await this.loadTrainingCounts();
          await this.handleApplyFilter();
        }
      } catch (error) {
        console.error("Error registering for training:", error);
      }
    },

    async initiatePurchase(id) {
      try {
        const result = await axios.post(
          API_ENDPOINTS.PAYMENT_INIT,
          {
            items: [{ id, type: "training" }],
          },
          { headers: this.apiHeaders }
        );

        if (result?.data?.data) {
          this.orderedItemListForBuying = result.data.data;
          this.token = result.data.data.token;
          this.$refs.creditCardModal.show();
        }
      } catch (error) {
        console.error("Error initiating purchase:", error);
      }
    },

    // Utility Methods
    isDemoActive(item) {
      return item.training_content?.some((content) => content.is_demo === 1);
    },

    getBottomTitle(percentage) {
      switch (percentage) {
        case 0:
          return this.$t("general.start");
        case 100:
          return this.$t("general.completed");
        default:
          return this.$t("general.continue");
      }
    },

    fixDate(value) {
      return moment(value)
        .locale(this.secureLocalStorage("language"))
        .format("DD MMMM YYYY");
    },
  },
};
</script>

<style scoped>

/* ==========================================================================
   Page Header
   ========================================================================== */

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-title i {
  font-size: 28px;
  color: var(--backgroundColor, #667eea);
}

.header-title h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #2d3748;
}

.header-search {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 200px;
  max-width: 320px;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 12px;
  font-size: 18px;
  color: #94a3b8;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--backgroundColor, #667eea);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

/* ==========================================================================
   Catalog Layout
   ========================================================================== */
.catalog-layout {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  align-items: start;
  position: relative;
  z-index: 1;
}

.catalog-filters-wrapper {
  position: relative;
}

.catalog-content {
  min-width: 0;
}

.catalog-content .card {
  background: #ffffff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
}

@media (max-width: 959px) {
  .catalog-content .card {
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    border: none;
  }
}

/* ==========================================================================
   Empty State
   ========================================================================== */
.empty-assignment-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-assignment-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.empty-assignment-icon i {
  font-size: 36px;
  color: #ffffff;
}

.empty-assignment-title {
  font-size: 18px;
  font-weight: 600;
  color: #4a5568;
  margin: 0;
}

/* ==========================================================================
   Responsive - Large Tablet
   ========================================================================== */
@media (max-width: 1199px) {
  .catalog-layout {
    grid-template-columns: 1fr 2fr;
    gap: 20px;
  }
}

/* ==========================================================================
   Responsive - Tablet
   ========================================================================== */
@media (max-width: 959px) {
  .education-catalog-page {
    padding: 0 !important;
  }

  .header-title i {
    font-size: 24px;
  }

  .header-title h3 {
    font-size: 18px;
  }

  .header-search {
    max-width: 100%;
  }

  .catalog-layout {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .catalog-filters-wrapper {
    width: 100%;
  }

  .catalog-content {
    width: 100%;
  }
}

/* ==========================================================================
   Responsive - Mobile
   ========================================================================== */
@media (max-width: 639px) {
  .education-catalog-page {
    padding: 12px;
  }

  .header-title i {
    font-size: 22px;
  }

  .header-title h3 {
    font-size: 16px;
  }

  .search-input {
    padding: 12px 12px 12px 40px;
    font-size: 16px;
  }

  .empty-assignment-state {
    padding: 40px 16px;
  }

  .empty-assignment-icon {
    width: 70px;
    height: 70px;
  }

  .empty-assignment-icon i {
    font-size: 32px;
  }

  .empty-assignment-title {
    font-size: 16px;
  }
}

/* ==========================================================================
   Responsive - Small Mobile
   ========================================================================== */
@media (max-width: 479px) {


  .header-title h3 {
    font-size: 15px;
  }
}

/* Mobile UI safeguards */
.education-catalog-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

@media (max-width: 959px) {
  .education-catalog-page :deep(.page-header-block) {
    gap: 12px;
  }

  .education-catalog-page :deep(.page-header-block__left),
  .education-catalog-page :deep(.page-header-block__right),
  .education-catalog-page :deep(.search-input-component) {
    min-width: 0;
  }

  .education-catalog-page :deep(.uk-search-input) {
    height: 48px;
    min-height: 48px;
    box-sizing: border-box;
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
    font-size: 16px;
    line-height: 1.5;
  }

  .education-catalog-page :deep(.clear-search-btn) {
    min-width: 44px;
    min-height: 44px;
    padding: 10px;
  }

  .education-catalog-page :deep(.catalog-content),
  .education-catalog-page :deep(.catalog-card-list),
  .education-catalog-page :deep(.catalog-card-list-skeleton) {
    min-width: 0;
    max-width: 100%;
  }
}

@media (max-width: 639px) {
  .education-catalog-page {
    padding-left: 12px;
    padding-right: 12px;
  }

  .education-catalog-page :deep(.page-header-block__title) {
    font-size: 18px;
  }

  .education-catalog-page :deep(.catalog-tabs-container) {
    border-radius: 12px;
    margin-bottom: 12px;
  }
}
</style>
